import { useCallback, useEffect, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserRole } from 'common/contracts';
import { UserDetails } from 'pages/Login/Login.types';

import { ELocalStorageKeys, ELogoutMessage } from '../constants/enums';
import useAxios from '../hooks/useAxios';
import { useLogin } from '../hooks/useLogin';
import { localStorageUtil } from '../utils/localStorageUtil';

import { EApiRoutes, getApiRouteOrMock } from './api.utils';
import usePermissions from './usePermissions';

interface IResolvedUserNames {
  firstName: string;
  lastName: string;
  userName: string;
}

function resolveUserNames(
  apiData: any,
  cachedUserDetails?: UserDetails | null
): IResolvedUserNames {
  const fullName = apiData?.name || apiData?.userName || '';
  const [firstNamePart, ...lastNameParts] = fullName.split(' ');

  const firstName =
    apiData?.firstName || firstNamePart || cachedUserDetails?.firstName || '';
  const lastName =
    apiData?.lastName ||
    lastNameParts.join(' ') ||
    cachedUserDetails?.lastName ||
    '';
  const userName =
    apiData?.userName ||
    apiData?.name ||
    `${apiData?.firstName || ''} ${apiData?.lastName || ''}`.trim() ||
    cachedUserDetails?.userName ||
    '';

  return { firstName, lastName, userName };
}

function transformToUserDetails(
  data: any,
  cachedUserDetails?: UserDetails | null
): UserDetails {
  const apiData = data?.result || data;

  const hasCompleteData =
    apiData?.projects !== undefined || apiData?.portals !== undefined;

  const { firstName, lastName, userName } = resolveUserNames(
    apiData,
    cachedUserDetails
  );

  const version = apiData?.version || cachedUserDetails?.version;
  const role = version
    ? UserRole.SUPER_ADMIN
    : apiData?.role || cachedUserDetails?.role;

  return {
    email: apiData?.email || cachedUserDetails?.email || '',
    firstName,
    lastName,
    userName,
    userId: apiData?.userId || apiData?._id || cachedUserDetails?.userId || '',
    role,
    projects: hasCompleteData ? apiData?.projects : cachedUserDetails?.projects,
    portals: hasCompleteData ? apiData?.portals : cachedUserDetails?.portals,
    version,
    company: apiData?.company || cachedUserDetails?.company,
    companyDisplayName:
      apiData?.companyDisplayName || cachedUserDetails?.companyDisplayName,
    isOnboarding:
      apiData?.isOnboarding !== undefined
        ? apiData?.isOnboarding
        : cachedUserDetails?.isOnboarding
  };
}

export function useUserDetails() {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { cleanUserLoginData } = useLogin();
  const { setPermissions, getPermissions } = usePermissions();

  const token = localStorageUtil.getAny(ELocalStorageKeys.JWT_TOKEN);

  const isLoggedIn = useMemo(() => {
    return Boolean(token);
  }, [token]);

  const storedUserDetails = localStorageUtil.getAny<UserDetails>(
    ELocalStorageKeys.USER_DETAILS
  );

  const initialData = useMemo(() => {
    const cachedData = queryClient.getQueryData<UserDetails>([
      EApiRoutes.USERS
    ]);
    if (cachedData) return cachedData;

    if (storedUserDetails) {
      queryClient.setQueryData([EApiRoutes.USERS], storedUserDetails);
      return storedUserDetails;
    }

    return null;
  }, [queryClient, storedUserDetails]);

  const getUsers = useQuery({
    queryKey: [EApiRoutes.USERS],
    staleTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: false,
    queryFn: async (): Promise<UserDetails> => {
      const freshToken = localStorageUtil.getAny(ELocalStorageKeys.JWT_TOKEN);
      const tokenTTL = localStorageUtil.getAny(ELocalStorageKeys.JWT_TOKEN_TTL);

      if (!freshToken) {
        throw new Error('No token available');
      }

      const isCookieAuthToken =
        typeof freshToken === 'string' && freshToken.startsWith('cookie-auth:');
      if (!isCookieAuthToken && tokenTTL && tokenTTL - Date.now() <= 0) {
        cleanUserLoginData(ELogoutMessage.SESSION_EXPIRED);
        throw new Error('Token expired');
      }

      try {
        const loginResponse = await axios.post(
          getApiRouteOrMock(EApiRoutes.LOGIN_USER_TOKEN),
          { accessToken: freshToken }
        );

        const loginData = loginResponse?.data || loginResponse;
        const freshUserDetails = transformToUserDetails(loginData);

        const isSessionToken = localStorageUtil.isSessionKey(
          ELocalStorageKeys.JWT_TOKEN
        );
        localStorageUtil.set(
          ELocalStorageKeys.USER_DETAILS,
          freshUserDetails,
          isSessionToken
        );

        return freshUserDetails;
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401 || status === 500) {
          cleanUserLoginData(ELogoutMessage.SESSION_EXPIRED);
        }
        throw error;
      }
    },
    enabled: isLoggedIn && !storedUserDetails
  });

  const userDetails = useMemo(() => {
    if (getUsers.data) {
      return getUsers.data as UserDetails;
    }

    return initialData ?? null;
  }, [getUsers.data, initialData]);

  useEffect(() => {
    if (userDetails?.projects) {
      const currentPermissions = getPermissions();
      if (!currentPermissions) {
        setPermissions();
      }
    }
  }, [userDetails?.projects, getPermissions, setPermissions]);

  const permissions = getPermissions();

  const isLoading =
    isLoggedIn &&
    ((getUsers.isLoading && !userDetails) ||
      (!!userDetails?.projects && !permissions));

  const persistUserDetails = useCallback(
    (details: UserDetails) => {
      queryClient.setQueryData([EApiRoutes.USERS], details);
      const isSession = localStorageUtil.isSessionKey(
        ELocalStorageKeys.USER_DETAILS
      );
      localStorageUtil.set(
        ELocalStorageKeys.USER_DETAILS,
        details,
        isSession
      );
    },
    [queryClient]
  );

  const refetchUserDetails = useCallback(async () => {
    try {
      const data = await axios.get(getApiRouteOrMock(EApiRoutes.USERS));
      const currentUserDetails = queryClient.getQueryData<UserDetails>([
        EApiRoutes.USERS
      ]);
      const freshUserDetails = transformToUserDetails(data, currentUserDetails);

      persistUserDetails(freshUserDetails);

      return freshUserDetails;
    } catch (error) {
      console.error('Failed to refetch user details:', error);
      throw error;
    }
  }, [axios, queryClient, persistUserDetails]);

  return {
    userDetails,
    isLoading,
    isError: getUsers.isError,
    error: getUsers.error,
    refetch: getUsers.refetch,
    refetchUserDetails,
    persistUserDetails
  };
}
